import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ll_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("ll_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
    // api / localStorage are module-level constants, no reactive deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
    // refresh is stable (useCallback with []), safe as mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Activity heartbeat: while a user is logged in AND actively interacting
  // (mouse/keyboard/touch within the past 90s) AND the tab is visible,
  // ping the backend every 60s so admins can see real study time — not
  // idle time when the user has walked away. Reports which page category
  // the user is on so admin reports can break down time-spent.
  useEffect(() => {
    if (!user) return undefined;
    const IDLE_MS = 90_000; // window of "still active" after last interaction
    let stopped = false;
    let lastActivity = Date.now();
    const markActive = () => { lastActivity = Date.now(); };
    const pageCategory = () => {
      const p = (typeof window !== "undefined" ? window.location.pathname : "") || "";
      if (p.startsWith("/learn")) return "learn";
      if (p.startsWith("/review")) return "review";
      if (p.startsWith("/create")) return "create";
      if (p.startsWith("/profile")) return "profile";
      if (p.startsWith("/admin")) return "admin";
      if (p.startsWith("/leaderboard") || p.startsWith("/ranks")) return "leaderboard";
      if (p.startsWith("/courses") || p.startsWith("/course")) return "courses";
      if (p === "/" || p.startsWith("/dashboard") || p.startsWith("/home")) return "home";
      return "other";
    };
    const ping = () => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (Date.now() - lastActivity > IDLE_MS) return; // user is idle — don't count
      api.post(`/activity/ping?page=${pageCategory()}`).catch(() => {});
    };
    ping(); // initial ping on login/mount (fresh activity)
    const id = setInterval(ping, 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible") {
        markActive(); // returning to the tab counts as fresh activity
        ping();
      }
    };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(id);
      evts.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.id]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ll_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("ll_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    // Best-effort cookie clear on the server; localStorage is our source of truth.
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ll_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
